import { useTranslation } from "react-i18next";
import { VERSION_BUMPS, type VersionPlan } from "../../../api/content/versionDecoders";
import type { LoadHandle } from "../../../shared/loading/useLoad";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import FieldGroup from "../../../ui/forms/FieldGroup";
import { FORM_COMPACT_CONTROL_CLASS, FORM_HELP_TEXT_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import { bumpText, planRowText } from "../labels/versionText";
import { chosenBump, planRows, withChoice, type BumpChoices } from "./versionConfirmation";

/** The change line the labels show; the server's `release_note` limit. */
const MAXIMUM_CHANGE_LINE = 2000;

interface MergeVersionFieldsProps {
  plan: LoadHandle<VersionPlan | null>;
  /** An item's name, for its row. */
  nameOf: (itemId: string) => string;
  choices: BumpChoices;
  onChoicesChange: (choices: BumpChoices) => void;
  changeLine: string;
  onChangeLineChange: (changeLine: string) => void;
}

/**
 * The person merging confirms or changes DMW's proposed version labels (ADR-0033 D3, built
 * provisionally, awaiting acceptance): one row per subject, the project content first, and
 * one change line for all of them.
 */
export default function MergeVersionFields({ plan, nameOf, choices, onChoicesChange, changeLine, onChangeLineChange }: MergeVersionFieldsProps) {
  const { t, i18n } = useTranslation();
  return (
    <FieldGroup legend={t("versions.merge.title")} className="space-y-2">
      {plan.state.status === "loaded" ? null : (
        <>
          <LoadStatus state={plan.state} loadingLabel={t("versions.merge.loading")} onRetry={plan.reload} compact={true} />
          {plan.state.status === "failed" ? <p className={FORM_HELP_TEXT_CLASS}>{t("versions.merge.planFailed")}</p> : null}
        </>
      )}
      {plan.state.status === "loaded" && plan.state.value !== null ? (
        <ul className="space-y-2 text-sm">
          {planRows(plan.state.value).map((row) => {
            const name = row.itemId === null ? t("versions.projectContent") : nameOf(row.itemId);
            const bump = chosenBump(row, choices);
            return (
              <li key={row.itemId ?? ""} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-semibold">{name}</span>
                <span className="app-text-muted min-w-0 flex-1">{planRowText(t, row, i18n.resolvedLanguage)}</span>
                {bump === null ? null : (
                  <select
                    className={`${FORM_SELECT_CLASS} w-auto`}
                    aria-label={t("versions.merge.bumpFor", { subject: name })}
                    value={bump}
                    onChange={(event) => {
                      const picked = VERSION_BUMPS.find((value) => value === event.target.value);
                      if (picked !== undefined) onChoicesChange(withChoice(choices, row, picked));
                    }}
                  >
                    {VERSION_BUMPS.map((value) => {
                      const text = value === row.proposal ? t("versions.merge.proposedBump", { bump: bumpText(t, value) }) : bumpText(t, value);
                      const gives = row.versionsByBump[value];
                      return (
                        <option key={value} value={value}>
                          {gives === undefined ? text : t("versions.merge.bumpGives", { bump: text, version: gives })}
                        </option>
                      );
                    })}
                  </select>
                )}
                {row.status === "bump" && row.mayChangeMethod ? <span className={`${FORM_HELP_TEXT_CLASS} basis-full`}>{t("versions.merge.mayChangeMethod")}</span> : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      <input
        className={FORM_COMPACT_CONTROL_CLASS}
        value={changeLine}
        maxLength={MAXIMUM_CHANGE_LINE}
        aria-label={t("versions.merge.changeLine")}
        placeholder={t("versions.merge.changeLine")}
        onChange={(event) => {
          onChangeLineChange(event.target.value);
        }}
      />
    </FieldGroup>
  );
}
